import * as THREE from 'three';
import { bladeAtlas, tuftGeometry, type Tuft } from './tufts.js';
import { foliage } from './world.js';

export { bladeAtlas, tuftGeometry };
export type { Tuft };

/**
 * Grass tufts (the island alive, Pieter 28 Sep 2026): low clumps of blades on
 * crossed cards, one instanced mesh for all of them, cut out with alpha to
 * coverage and swaying like the ferns. The blades are drawn in code into one
 * small texture, so no file is needed.
 */

/**
 * Every tuft in one instanced mesh, swaying in the world's wind; the blades
 * drawn in code, or another card texture (a meadow's flowering tufts, part 2).
 */
export function grassTufts(
  tufts: readonly Tuft[],
  wind: THREE.IUniform<number>,
  options: { map?: THREE.Texture; name?: string; sway?: number } = {},
): THREE.InstancedMesh {
  const material = new THREE.MeshStandardMaterial({
    map: options.map ?? bladeAtlas(),
    alphaTest: 0.5,
    roughness: 0.95,
    metalness: 0,
  });
  // Blades bend a few centimetres at their tips (in the card's own metre).
  foliage(material, options.sway ?? 0.12, { wind, base: 0, height: 1 });
  const mesh = new THREE.InstancedMesh(tuftGeometry(), material, tufts.length);
  mesh.name = options.name ?? 'grass-tufts';
  const matrix = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  const colour = new THREE.Color();
  tufts.forEach((tuft, i) => {
    q.setFromAxisAngle(up, THREE.MathUtils.degToRad(tuft.turn));
    matrix.compose(
      new THREE.Vector3(...tuft.pos),
      q,
      new THREE.Vector3(tuft.width, tuft.height, tuft.width),
    );
    mesh.setMatrixAt(i, matrix);
    mesh.setColorAt(i, colour.setScalar(tuft.shade));
  });
  mesh.computeBoundingSphere();
  return mesh;
}
